import React, { useEffect, useRef, useState } from 'react';
import {
  Box, HStack, Stack, Switch, Text, useColorModeValue, useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { Invoice } from '../models/invoice';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

type Output = {
  itemize: boolean;
  showPerformedOnPdf: boolean;
  includeScopeOnPdf: boolean;
  includeWorkPerformedOnPdf: boolean;
};

// item 1503 — how long the green "Saved" rests after a flip before the badge clears
const SAVED_BADGE_MS = 2000;

// item 1488 (Chad: "PDF output settings could be updated after finalize") — the invoice's
// output toggles stay live on a FINALIZED invoice. They are presentation only (totals, QB export
// and commissions never read them), so each flip saves straight away via Invoice/{id}/pdf-output.
// Rendered above the locked body; the draft form edits the same four fields in Item Settings.
const InvoicePdfOutputPanel: React.FC<{ invoice: Invoice }> = ({ invoice }) => {
  const qc = useQueryClient();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  // item 1503 (Chad) — each flip saved SILENTLY; the same Saving…/Saved narration the document
  // forms show (DocumentFormScaffold's AutoSaveBadge look) now sits beside the panel title.
  const [badge, setBadge] = useState<'idle' | 'saving' | 'saved'>('idle');
  const badgeTimer = useRef<number | null>(null);
  useEffect(() => () => { if (badgeTimer.current) window.clearTimeout(badgeTimer.current); }, []);
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const help = useColorModeValue('gray.500', 'whiteAlpha.600');
  // item 958 — light-mode contrast: gray.400 read as ghost text on light glass; one step darker in light only
  const quiet = useColorModeValue('gray.500', 'gray.400');
  const isService = !invoice.proposalId;
  const current: Output = {
    itemize: !!invoice.itemize,
    showPerformedOnPdf: !!invoice.showPerformedOnPdf,
    includeScopeOnPdf: !!invoice.includeScopeOnPdf,
    includeWorkPerformedOnPdf: !!invoice.includeWorkPerformedOnPdf,
  };

  const flip = async (key: keyof Output, value: boolean) => {
    setSaving(true);
    if (badgeTimer.current) { window.clearTimeout(badgeTimer.current); badgeTimer.current = null; }
    setBadge('saving');
    try {
      await axios.post(`${apiUrl()}Invoice/${invoice.id}/pdf-output`, { ...current, [key]: value });
      await qc.invalidateQueries(['invoice', invoice.id]);
      setBadge('saved');
      badgeTimer.current = window.setTimeout(() => { setBadge('idle'); badgeTimer.current = null; }, SAVED_BADGE_MS);
    } catch (e: any) {
      setBadge('idle');
      toast({ status: 'error', title: 'PDF output not saved', description: e?.response?.data?.errors?.[0] ?? 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const rows: { key: keyof Output; label: string; help: string }[] = [
    { key: 'itemize', label: 'Itemize lines (group price)', help: 'Each line shows its price and amount. Off: SKU and quantity only.' },
    { key: 'showPerformedOnPdf', label: 'Show who performed', help: 'Adds the name beside each line’s date (dates always print).' },
    {
      key: 'includeScopeOnPdf',
      label: isService ? 'Include reason for work' : 'Include proposal scope',
      help: isService ? 'Prints the client’s request.' : 'Prints the proposal’s statement of work.',
    },
    { key: 'includeWorkPerformedOnPdf', label: 'Include work performed', help: 'Prints the service notes (nothing prints when blank).' },
  ];

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={4} mb={4}>
      <HStack justify="space-between" mb={2}>
        <HStack spacing={3}>
          <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em">
            {`PDF OUTPUT · ${isService ? 'SERVICE' : 'PROJECT'} INVOICE`}
          </Text>
          {/* item 1503 — Saving… / Saved feedback, same look as the form's auto-save pill */}
          {badge === 'saving' && <Text fontSize="xs" color={quiet} fontWeight="medium">Saving…</Text>}
          {badge === 'saved' && <Text fontSize="xs" color="green.400" fontWeight="medium">Saved</Text>}
        </HStack>
        <Text fontSize="xs" color={help}>Still editable after finalize — changes the PDF only, never the totals</Text>
      </HStack>
      <Stack spacing={2}>
        {rows.map((r) => (
          <HStack key={r.key} justify="space-between" align="flex-start">
            <Box>
              <Text fontSize="sm">{r.label}</Text>
              <Text fontSize="xs" color={help}>{r.help}</Text>
            </Box>
            <Switch
              isChecked={current[r.key]}
              isDisabled={saving}
              onChange={(e) => flip(r.key, e.target.checked)}
            />
          </HStack>
        ))}
      </Stack>
    </Box>
  );
};

export default InvoicePdfOutputPanel;
